/* eslint-disable jsx-a11y/anchor-is-valid */
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
	Box,
	Card,
	IconButton,
	InputAdornment,
	LinearProgress,
} from '@mui/material';
import { Form, Formik } from 'formik';
import { GoogleLogin as GoogleLoginButton, GoogleOAuthProvider } from '@react-oauth/google';
import { decodeToken } from 'react-jwt';
import { React, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import * as yup from 'yup';
import { BASE_URLS, GOOGLE_CLIENT_ID, IS_SA } from '../../../config/appConfig';
import { AppRoutes } from '../../../constants/appRoutes';
import { InvestorRegistrationStep } from '../../../constants/investorRegistrationSteps';
import { InvestorStatus } from '../../../constants/investorStatus';
import { LocalStorageKey } from '../../../constants/localStorageKeys';
import { OTPTypes } from '../../../constants/otpTypes';
import { Roles } from '../../../constants/roles';
import { getCurrentUserPath } from '../../../helpers/authHelper';
import { buildLocalizeRoute } from '../../../helpers/languageHelper';
import { checkIsBorrowerOrInvestor } from '../../../helpers/loginCheckHelper';
import { MarketplaceService } from '../../../pages/investor/views/marketplace/marketplaceService';
import { Platform } from '../../../pages/registration/components/platform';
import { RegistrationNavBar } from '../../../pages/registration/components/registrationNavBar';
import { Logout, getUser, googleLogin, login } from '../../../services/authService';
import { LanguageService } from '../../../services/languageService';
import { SystemSettingsService } from '../../../services/systemSettingsService';
import { UserService } from '../../../services/userService';
import { ButtonWrapper } from '../../../shared/components/formUtilities/useButton';
import { InputWrapper } from '../../../shared/components/formUtilities/useInput';
import { TextWrapper } from '../../../shared/components/formUtilities/useTextWrapper';
import { OTPAuthenticationDialog } from '../../../shared/components/otpAuthenticationDialog';
import { RegistrationDialog } from '../components/registrationDialog';
import { UserLockOutDialog } from '../components/userLockoutDialog';
import { Toaster } from '../../../shared/components/common/toaster';
import { useDispatch, useSelector } from 'react-redux';
import { setSytemSettingsData } from '../../../redux/action';

function Login({ isLoading = false }) {
	const navigate = useNavigate();
	const systemSettingsService = new SystemSettingsService();
	const languageService = new LanguageService();
	const marketplaceService = new MarketplaceService();
	const userService = new UserService();
	const [passwordType, setPasswordType] = useState('password');
	const { t } = useTranslation();

	const [loading, setLoading] = useState(isLoading);
	const [isUserLockOut, setIsUserLockOut] = useState(false);
	const [isShowOtpScreen, setIsShowOtpScreen] = useState(false);
	const [errorCounter, setErrorCounter] = useState(100);
	const [timerId, setTimerId] = useState(null);
	const dispatch = useDispatch();
	const [isShowEmailNotFoundError, setIsShowEmailNotFoundError] =
		useState(false);
	const [loginResp, setLoginResp] = useState({});
	const [showRegDialog, setShowRegDialog] = useState(false);
	const enableGoogleSSO = useSelector(
		(state) => state.systemSettingsDataReducer.settings?.enableGoogleSSO
	);
	const showGoogleSignIn = enableGoogleSSO === true && !!GOOGLE_CLIENT_ID;

	// eslint-disable-next-line no-unused-vars
	const [searchParams, setSearchParams] = useSearchParams();

	useEffect(() => {
		localStorage.removeItem('accessDenied');
		systemSettingsService
			.getAllFront()
			.then((resp) => {
				dispatch(setSytemSettingsData(resp));
			})
			.catch((resp) => { });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const returnUrl = params.get('returnUrl');
		const lang = window.location.pathname.includes('/ar/') ? 'ar' : 'en';
		const isSA = window.location.pathname.includes('/sa/');
		const baseUrl = window.location.origin;
		if (returnUrl && returnUrl.includes(AppRoutes.INafathVerification) && !isSA) {
			const url = `${baseUrl}/sa/${lang}/login/?returnUrl=investor/profile/nafath-verification`;
			setTimeout(() => {
				window.location.href = url;
			}, 200);
		}
	}, []);

	useEffect(() => {
		if (isShowEmailNotFoundError) {
			startCountdown();
		}
	}, [isShowEmailNotFoundError]);

	const startCountdown = () => {
		setErrorCounter(100);
		const id = setInterval(() => {
			setErrorCounter((countdown) => countdown - 1);
		}, 100);
		setTimerId(id);
	};

	useEffect(() => {
		if (errorCounter <= 0) {
			setErrorCounter(100);
			setIsShowEmailNotFoundError(false);
			clearInterval(timerId);
		}
	}, [errorCounter, timerId]);

	useEffect(() => {
		return () => clearInterval(timerId);
	}, [timerId]);

	const togglePassword = () => {
		if (passwordType === 'password') {
			setPasswordType('text');
			return;
		}
		setPasswordType('password');
	};

	const isAr = () => languageService.isArabic();

	const loginSchema = yup.object({
		email: yup.string().email().required(t('required')),
		password: yup.string().required(t('required')),
	});

	const initialValues = {
		email: '',
		password: '',
	};

	const onSubmit = (values, e) => {
		setLoading(true);
		login(values)
			.then((loginResp) => {
				setLoginResp(loginResp.data);
				afterLogin(loginResp);
			})
			.catch((err) => {
				setIsUserLockOut(checkIfUserLockOut(err));
				if (err?.response?.data?.EmailNotConfirmed) {
					setTimeout(() => {
						navigate(buildLocalizeRoute(`${AppRoutes.VerifyEmail}?e=${encodeURIComponent(values.email)}&type=1`));
					}, 300);
					return;
				}
				if (
					err?.response?.data?.loginEmailNotFound &&
					!checkIfUserLockOut(err)
				) {
					setIsShowEmailNotFoundError(true);
				} else {
					setIsShowEmailNotFoundError(false);
				}

				setLoading(false);
			});
	};

	const onGoogleLoginSuccess = (credentialResponse) => {
		setLoading(true);
		googleLogin(credentialResponse.credential)
			.then((loginResp) => {
				if (!loginResp) {
					setLoading(false);
					return;
				}
				setLoginResp(loginResp.data);
				afterLogin(loginResp);
			})
			.catch((err) => {
				if (err?.response?.data?.EmailNotConfirmed) {
					const googlePayload = decodeToken(credentialResponse.credential);
					setTimeout(() => {
						navigate(buildLocalizeRoute(`${AppRoutes.VerifyEmail}?e=${encodeURIComponent(googlePayload?.email || '')}&type=1`));
					}, 300);
					return;
				}
				setLoading(false);
				if (err?.response?.data?.NoAccountFoundForGoogleEmail) {
					Toaster({ msg: t('googleNoAccountFound') });
					return;
				}
				Toaster({ msg: t('googleSignInFailed') });
			});
	};

	const onGoogleLoginError = () => {
		Toaster({ msg: t('googleSignInFailed') });
	};

	const getHasMarketplace = async () => {
		try {
			setLoading(true);
			const resp = await marketplaceService.getMarketplace();
			const isMarketplace = resp.data && resp.data.length > 0;
			setLoading(false);
			return isMarketplace;
		}
		catch {
			setLoading(false);
			return false;
		}
	};

	const afterLogin = (loginResponse) => {
		if (loginResponse.data?.otpRequested) {
			setTimeout(() => {
				showOtpVerification();
			});
		} else {
			setTimeout(() => {
				afterLoginSuccess(loginResponse.data);
			});
		}
	};

	const showOtpVerification = () => {
		setIsShowOtpScreen(true);
	};

	const afterLoginSuccess = async (data = null) => {
		const user = getUser();
		const isSA = window.location.pathname.includes('/sa/');
		let loginData = data ? data : loginResp;
		let isMarketplace = false;
		const isInvestor = loginData?.roles?.find(role => role === Roles.Investor);

		const checkPointData = await userService.getCheckPoint();
		if (checkPointData) {
			localStorage.setItem(LocalStorageKey.checkPointServey, JSON.stringify(checkPointData.data));
		}

		if (loginData.mustResetPassword) {
			navigate(buildLocalizeRoute(AppRoutes.PasswordExpired));
			return;
		}

		if (user?.status === InvestorStatus.Archived && !user?.isApproved && isInvestor) {
			let url = `${AppRoutes.Investor}/${AppRoutes.ThankYou}`;
			navigate(buildLocalizeRoute(url));
			return;
		}

		if(isSA && isInvestor && loginData.showNafathPage) {
			let url = `${AppRoutes.Investor}/${AppRoutes.Profile}/${AppRoutes.INafathVerification}`;
			navigate(buildLocalizeRoute(url));
			return;
		}

		if (isInvestor && loginData?.investorStep >= InvestorRegistrationStep.Declaration) {
			isMarketplace = await getHasMarketplace();
		}


		let route = checkIsBorrowerOrInvestor(loginData, user);
		if (route) {

			if (route === AppRoutes.Investor && isMarketplace) {
				navigate(buildLocalizeRoute(`${AppRoutes.Investor}/${AppRoutes.Marketplace}`));
				return;
			}

			navigate(buildLocalizeRoute(route));
			return;
		}

		if (!user?.isApproved) {
			if (user?.status === InvestorStatus.WindDown && !isInvestor) {
				navigate(buildLocalizeRoute(AppRoutes.WindDown));
				return;
			}
			redirectToThankYouPage(loginData?.roles, isMarketplace);
		} else {
			onNavigateToDashboard(isMarketplace);
		}
	};

	const redirectToThankYouPage = (roles = [], isMarketplace = false) => {
		if (roles.find((resp) => resp === Roles.Investor)) {
			onNavigateToDashboard(isMarketplace);
			return;
		}
		let url = `${AppRoutes.Borrower}/${AppRoutes.ThankYou}`;
		navigate(buildLocalizeRoute(url));
		return;
	};

	const onNavigateToDashboard = async (isMarketplace = false) => {
		const returnUrl = searchParams.get('returnUrl');
		let route = getCurrentUserPath(returnUrl);
		if (route === AppRoutes.Investor && isMarketplace) {
			navigate(buildLocalizeRoute(`${AppRoutes.Investor}/${AppRoutes.Marketplace}`));
			return;
		}
		navigate(route);
	};

	const checkIfUserLockOut = (err) => {
		let response = err?.response || null;
		if (response && response.status === 400 && response?.data?.UserLockedOut) {
			return true;
		}

		return false;
	};

	const getLoginUrl = () => {
		let baseURL = !IS_SA ? BASE_URLS.sa : BASE_URLS.en;
		return `${baseURL}/${languageService.getCurrentLanguage()}/login`;
	};

	return (
		<>
			<RegistrationNavBar isLogin={true}></RegistrationNavBar>
			<div className="flex flex-auto relative flex-col items-center justify-center min-w-0 md:h-[calc(100%-130px)] auth-card-wrapper bg-white p-3 md:p-0">
				<Box
					component={Card}
					className="flex md:flex-row flex-col !rounded-lg h-auto overflow-auto w-full md:w-auto">
					<Box className="flex relative md:flex-auto md:h-auto h-full flex-col md:py-12 md:px-12 p-6 bg-white md:w-[400px] w-full">
						<Box
							className={`absolute ${isAr() ? 'left-[24px]' : 'right-[24px]'
								}   top-[24px]`}>
							<Platform />
						</Box>
						<TextWrapper
							classList="!text-[24px] font-bold !text-center !mt-[34px] md:!mt-[22px] !mb-1"
							type="h6"
							text={t('welcomeBack')}
						/>
						<TextWrapper
							classList="!text-[15px] !caption !text-center !mb-2"
							type="h6"
							text={t('signInToYourAccount')}
						/>
						<Formik
							initialValues={initialValues}
							onSubmit={onSubmit}
							validationSchema={loginSchema}
							validateOnMount={true}
							enableReinitialize={true}>
							{(formik) => {
								return (
									<Form className="mt-3">
										<Box className="grid grid-rows-2 gap-6">
											<InputWrapper
												errorStateTestID="emailError"
												key={'email'}
												label={'email'}
												size="small"
												error={formik.errors.email && formik.touched.email}
												helperText={
													formik.errors.email && formik.touched.email
														? t(formik.errors.email)
														: ''
												}
												variant="outlined"
												fullWidth={true}
												type={'email'}
												name="email"></InputWrapper>
											<InputWrapper
												errorStateTestID="passwordError"
												error={
													formik.errors.password && formik.touched.password
												}
												helperText={
													formik.errors.password && formik.touched.password
														? t(formik.errors.password)
														: ''
												}
												fullWidth={true}
												InputProps={{
													endAdornment: (
														<InputAdornment position="end">
															<IconButton
																className="!bg-transparent text-primary"
																onClick={togglePassword}>
																{passwordType === 'password' ? (
																	<VisibilityOffOutlinedIcon></VisibilityOffOutlinedIcon>
																) : (
																	<VisibilityOutlinedIcon></VisibilityOutlinedIcon>
																)}
															</IconButton>
														</InputAdornment>
													),
												}}
												key={'password'}
												label={'password'}
												variant="outlined"
												type={passwordType}
												placeholder={t('enterPassword')}
												name="password"
												size="small"
											/>
										</Box>
										{isShowEmailNotFoundError ? (
											<Box className="mt-6">
												<LinearProgress
													color="error"
													variant="determinate"
													value={errorCounter}
												/>
												<Box className="text-[#d32f2f] !text-[0.75rem] mt-1">
													<span>
														<span>{t('loginEmailNotFound')}</span>
														&nbsp;
														<a
															className="!text-[0.75rem] !text-[#d32f2f] underline"
															href={getLoginUrl()}>
															{t('here')}
														</a>
													</span>
												</Box>
											</Box>
										) : null}
										<ButtonWrapper
											dataTestID="signInBtn"
											icon={<LockOpenOutlinedIcon />}
											loading={loading}
											label={t('signIn')}
											classList="w-full !mt-6"
											type="submit"></ButtonWrapper>
										{showGoogleSignIn ? (
											<>
												<Box className="flex items-center mt-4">
													<Box className="flex-1 border-t border-gray-200" />
													<TextWrapper
														classList="!text-[12px] !caption !mx-3"
														type="caption"
														text={t('or')}
													/>
													<Box className="flex-1 border-t border-gray-200" />
												</Box>
												<Box className="flex justify-center mt-4" data-testid="googleSignInBtn">
													<GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
														<GoogleLoginButton
															onSuccess={onGoogleLoginSuccess}
															onError={onGoogleLoginError}
															width={300}
															text="signin_with"
														/>
													</GoogleOAuthProvider>
												</Box>
											</>
										) : null}
										<Box className="text-center mt-6">
											<Link
												data-testid="forgotPasswordLink"
												className="no-underline"
												to={`${buildLocalizeRoute(AppRoutes.ForgotPassword)}`}>
												<span className="underline-link">
													{t('loginForgotPassword')}
												</span>
											</Link>
										</Box>
										<Box className="text-center mt-1 text-[14px]">
											<span>{t('dontHaveAnAccount')}</span>
											<span
												data-testid="signUpLink"
												onClick={() => {
													setShowRegDialog(true);
												}}
												className="underline-link text-primary mx-1">
												{t('signUpInLogin')}
											</span>
										</Box>
									</Form>
								);
							}}
						</Formik>
					</Box>
				</Box>
			</div>
			{isUserLockOut ? (
				<UserLockOutDialog
					state={isUserLockOut}
					onClose={() => {
						setIsUserLockOut(false);
					}}></UserLockOutDialog>
			) : null}

			{isShowOtpScreen ? (
				<OTPAuthenticationDialog
					roles={loginResp?.roles}
					type={OTPTypes.Authentication}
					onClose={(isVerified) => {
						setIsShowOtpScreen(false);
						setLoading(false);
						if (isVerified) {
							afterLoginSuccess();
						} else {
							Logout();
						}
					}}
					state={isShowOtpScreen}></OTPAuthenticationDialog>
			) : null}
			{showRegDialog ? (
				<RegistrationDialog
					state={showRegDialog}
					onClose={(e) => {
						setShowRegDialog(false);
					}}
				/>
			) : null}
		</>
	);
}

export default Login;
